import { useApolloClient } from '@apollo/client';
import { CircularProgress, Text } from '@shared/components';
import {
  ChatThreadFilter,
  ChatViewContextGetter,
} from '@shared/contexts/ChatWindowContext';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import {
  ConversationDocument,
  ConversationType,
  ConversationUpdatedSubscription,
  ConversationUpdateType,
  GetChatThreadsDocument,
  useConversationUpdatedSubscription,
} from '@shared/generated/graphql';
import { useConciergeContext } from '@shared/plugin/contexts/ConciergeContext';
import { assertUnreachable } from 'clerk_common/enum';
import clsx from 'clsx';
import { DocumentNode } from 'graphql';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { LuMessageSquare } from 'react-icons/lu';
import { useLocalStorage } from 'usehooks-ts';
import { MessageDraft } from './components/ChatMessageInput';
import { ChatThreadList } from './components/ChatThreadList';
import { ChatThreadPanel } from './components/ChatThreadPanel';
import { NewChatPanel } from './components/NewChatPanel';
import { ThreadHeader } from './components/ThreadHeader';
import { ThreadListToggle } from './components/ThreadListToggle';

const CONVERSATION_UPDATE_REFETCH_DELAY_MS = 1000;
const THREAD_LIST_COLLAPSED_STORAGE_KEY = 'chats-page-thread-list-collapsed';

type ChatsPageProps = {
  conversationId: string | null;
  conversationListRequestCount?: number;
  getSuggestionContext?: ChatViewContextGetter;
  getTaskUrl?: (actionExecutionId: string) => string;
  getViewContext?: ChatViewContextGetter;
  hasTabCompletion?: boolean;
  hasUrlFilters?: boolean;
  headerTrailingAction?: ReactNode;
  inputBanner?: ReactNode;
  isCompact?: boolean;
  isLoadingConversation?: boolean;
  newChatRequestCount?: number;
  onConversationSelect: (conversationId: string | null) => void;
  onNewMessageInSelectedConversation?: () => void;
  threadFilter?: ChatThreadFilter | null;
};

export function ChatsPage({
  conversationId,
  conversationListRequestCount = 0,
  getSuggestionContext,
  getTaskUrl,
  getViewContext,
  hasTabCompletion = true,
  hasUrlFilters = true,
  headerTrailingAction,
  inputBanner,
  isCompact = false,
  isLoadingConversation = false,
  newChatRequestCount = 0,
  onConversationSelect,
  onNewMessageInSelectedConversation,
  threadFilter = null,
}: ChatsPageProps) {
  const apolloClient = useApolloClient();
  const { hasAccessToConcierge } = useConciergeContext();
  const { defaultOrgId } = useMeContext();
  const [, setHasActiveFilters] = useState(false);
  const [isComposingNewChat, setIsComposingNewChat] = useState(
    isCompact && !conversationId && !threadFilter
  );
  // NOTE(aman): /chats renders on the server, so reading storage on the first
  // render would cause a hydration mismatch.
  const [isThreadListCollapsed, setIsThreadListCollapsed] = useLocalStorage(
    THREAD_LIST_COLLAPSED_STORAGE_KEY,
    false,
    { initializeWithValue: false }
  );
  const [startedConversationId, setStartedConversationId] = useState<
    string | null
  >(null);
  const [pendingDraft, setPendingDraft] = useState<{
    conversationId: string;
    draft: MessageDraft;
  } | null>(null);
  const previousNewChatRequestCount = useRef(newChatRequestCount);
  const previousConversationListRequestCount = useRef(
    conversationListRequestCount
  );
  const pendingRefetchQueries = useRef(new Set<DocumentNode>());
  const previousDefaultOrgId = useRef(defaultOrgId);
  const refetchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const enqueueConversationUpdate = useCallback(
    (
      conversationUpdate: ConversationUpdatedSubscription['conversationUpdated']
    ) => {
      if (
        conversationUpdate.organizationId !== defaultOrgId ||
        conversationUpdate.conversationType !== ConversationType.CHAT
      ) {
        return;
      }
      if (
        conversationUpdate.type === ConversationUpdateType.MESSAGE_CREATED &&
        conversationUpdate.conversationId === conversationId
      ) {
        onNewMessageInSelectedConversation?.();
      }
      getConversationUpdateRefetchQueries(
        conversationUpdate,
        conversationId
      ).forEach((query) => pendingRefetchQueries.current.add(query));
      if (
        pendingRefetchQueries.current.size === 0 ||
        refetchTimeout.current !== undefined
      ) {
        return;
      }
      refetchTimeout.current = setTimeout(() => {
        const include = Array.from(pendingRefetchQueries.current);
        pendingRefetchQueries.current.clear();
        refetchTimeout.current = undefined;
        apolloClient.refetchQueries({ include }).catch(console.error);
      }, CONVERSATION_UPDATE_REFETCH_DELAY_MS);
    },
    [
      apolloClient,
      conversationId,
      defaultOrgId,
      onNewMessageInSelectedConversation,
    ]
  );

  useConversationUpdatedSubscription({
    skip: !defaultOrgId,
    variables: { organizationId: defaultOrgId ?? '' },
    onData: ({ data }) => {
      const conversationUpdate = data.data?.conversationUpdated;
      if (conversationUpdate) enqueueConversationUpdate(conversationUpdate);
    },
  });

  useEffect(() => {
    const previousOrganizationId = previousDefaultOrgId.current;
    if (previousOrganizationId === defaultOrgId) return;

    previousDefaultOrgId.current = defaultOrgId;
    if (previousOrganizationId && defaultOrgId) {
      onConversationSelect(null);
    }
    clearTimeout(refetchTimeout.current);
    refetchTimeout.current = undefined;
    pendingRefetchQueries.current.clear();
  }, [defaultOrgId, onConversationSelect]);

  useEffect(() => {
    if (previousNewChatRequestCount.current === newChatRequestCount) return;
    previousNewChatRequestCount.current = newChatRequestCount;
    setIsComposingNewChat(true);
  }, [newChatRequestCount]);

  useEffect(() => {
    if (
      previousConversationListRequestCount.current ===
      conversationListRequestCount
    ) {
      return;
    }
    previousConversationListRequestCount.current = conversationListRequestCount;
    setIsComposingNewChat(false);
  }, [conversationListRequestCount]);

  useEffect(
    () => () => {
      clearTimeout(refetchTimeout.current);
    },
    []
  );

  const handleConversationSelect = useCallback(
    (selectedConversationId: string | null) => {
      setIsComposingNewChat(false);
      onConversationSelect(selectedConversationId);
    },
    [onConversationSelect]
  );

  const handleConversationStarted = useCallback(
    (startedId: string, draft: MessageDraft) => {
      setPendingDraft({ conversationId: startedId, draft });
      setStartedConversationId(startedId);
      apolloClient
        .refetchQueries({ include: [GetChatThreadsDocument] })
        .catch(console.error);
      handleConversationSelect(startedId);
    },
    [apolloClient, handleConversationSelect]
  );
  const clearPendingDraft = useCallback(() => setPendingDraft(null), []);
  const clearStartedConversation = useCallback(
    () => setStartedConversationId(null),
    []
  );

  const handleNewChat = () => {
    setIsComposingNewChat(true);
    onConversationSelect(null);
  };

  const isThreadOpen = Boolean(conversationId) || isComposingNewChat;
  const shouldShowThreadList = !isCompact || !isThreadOpen;
  const shouldShowThreadPanel = !isCompact || isThreadOpen;
  const threadListToggle = isCompact ? undefined : (
    <ThreadListToggle
      isThreadListCollapsed={isThreadListCollapsed}
      onToggle={() => setIsThreadListCollapsed((isCollapsed) => !isCollapsed)}
    />
  );

  return (
    <div className="flex h-full w-full bg-white">
      {shouldShowThreadList && (
        <aside
          inert={!isCompact && isThreadListCollapsed}
          className={clsx(
            'flex shrink-0 flex-col',
            isCompact
              ? 'w-full bg-white'
              : [
                  'overflow-hidden border-r bg-[#fdfdfd] transition-[width,border-color] duration-500 motion-reduce:transition-none',
                  isThreadListCollapsed
                    ? 'w-0 border-transparent'
                    : 'w-80 border-gray-200',
                ]
          )}
        >
          <div
            className={clsx(
              'flex min-h-0 flex-1 flex-col',
              !isCompact && 'w-80 shrink-0'
            )}
          >
            {headerTrailingAction && (
              <ThreadHeader
                isCompact={isCompact}
                trailingAction={headerTrailingAction}
              />
            )}
            <ChatThreadList
              activeConversationId={conversationId}
              onSelect={handleConversationSelect}
              onNewChat={handleNewChat}
              canCreateChat={hasAccessToConcierge}
              hasUrlFilters={hasUrlFilters}
              isCompact={isCompact}
              isNewChatActive={
                !conversationId && (isComposingNewChat || !isCompact)
              }
              onActiveFiltersChange={setHasActiveFilters}
              pendingConversationId={startedConversationId}
              onPendingConversationListed={clearStartedConversation}
              threadFilter={threadFilter}
            />
          </div>
        </aside>
      )}
      {shouldShowThreadPanel && (
        <section className="flex min-w-0 flex-1 flex-col">
          {isLoadingConversation ? (
            <LoadingThreadState
              isCompact={isCompact}
              leadingAction={threadListToggle}
              onBack={
                isCompact ? () => handleConversationSelect(null) : undefined
              }
              trailingAction={headerTrailingAction}
            />
          ) : conversationId ? (
            <ChatThreadPanel
              conversationId={conversationId}
              getTaskUrl={getTaskUrl}
              getViewContext={getViewContext}
              inputBanner={inputBanner}
              initialDraft={
                pendingDraft?.conversationId === conversationId
                  ? pendingDraft.draft
                  : undefined
              }
              isCompact={isCompact}
              leadingAction={threadListToggle}
              onBack={
                isCompact ? () => handleConversationSelect(null) : undefined
              }
              onInitialDraftConsumed={clearPendingDraft}
              trailingAction={headerTrailingAction}
            />
          ) : hasAccessToConcierge ? (
            <NewChatPanel
              getSuggestionContext={getSuggestionContext}
              getViewContext={getViewContext}
              hasTabCompletion={hasTabCompletion}
              inputBanner={inputBanner}
              isCompact={isCompact}
              leadingAction={threadListToggle}
              onBack={
                isCompact ? () => handleConversationSelect(null) : undefined
              }
              onConversationStarted={handleConversationStarted}
              trailingAction={headerTrailingAction}
            />
          ) : (
            <EmptyThreadState
              leadingAction={threadListToggle}
              trailingAction={headerTrailingAction}
            />
          )}
        </section>
      )}
    </div>
  );
}

function getConversationUpdateRefetchQueries(
  update: ConversationUpdatedSubscription['conversationUpdated'],
  selectedConversationId: string | null
): DocumentNode[] {
  const isSelectedConversation =
    update.conversationId === selectedConversationId;
  switch (update.type) {
    case ConversationUpdateType.CONVERSATION_CREATED:
    case ConversationUpdateType.MESSAGE_CREATED:
      return [GetChatThreadsDocument];
    case ConversationUpdateType.MESSAGE_UPDATED:
      return [];
    case ConversationUpdateType.TITLE_UPDATED:
      return isSelectedConversation
        ? [GetChatThreadsDocument, ConversationDocument]
        : [GetChatThreadsDocument];
    default:
      return assertUnreachable(update.type);
  }
}

function LoadingThreadState({
  isCompact,
  leadingAction,
  onBack,
  trailingAction,
}: {
  isCompact: boolean;
  leadingAction?: ReactNode;
  onBack?: () => void;
  trailingAction?: ReactNode;
}) {
  return (
    <div className="flex h-full flex-col">
      <ThreadHeader
        isCompact={isCompact}
        leadingAction={leadingAction}
        onBack={onBack}
        trailingAction={trailingAction}
      />
      <div className="flex flex-1 items-center justify-center">
        <CircularProgress />
      </div>
    </div>
  );
}

function EmptyThreadState({
  leadingAction,
  trailingAction,
}: {
  leadingAction?: ReactNode;
  trailingAction?: ReactNode;
}) {
  return (
    <div className="flex h-full flex-col">
      {(leadingAction || trailingAction) && (
        <ThreadHeader
          leadingAction={leadingAction}
          trailingAction={trailingAction}
        />
      )}
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8">
        <LuMessageSquare
          className="text-gray-300"
          size={40}
          aria-hidden={true}
        />
        <Text type="body-sm" className="text-center text-gray-500">
          Select a chat from the list to view the conversation.
        </Text>
      </div>
    </div>
  );
}
